import {
  retrievePersistentData,
  storePersistentData
} from '@smart-tv/utils/persistent-data/persistent-data';

declare const navigator: any; // override type since navigator.deviceMemory is not defined in the typescript lib

const PERFORMANCE_SCORE_KEY = 'performanceScore-norlysplay';

/**
 * Calculates the performance score based on device memory, network downlink, and hardware concurrency.
 * The score is set to 'low' if the network downlink is poor (< 0.5).
 * If the tizenVersion is 6 or above, the score is set to 'high' unless the network is poor.
 * If the tizenVersion is below 6, the score is set to 'low'.
 * Otherwise, the score is determined by the average of memory and CPU scores.
 * The score is stored in localStorage.
 *
 * @param {string} [tizenVersion] - The Tizen version.
 */
const calculatePerformanceScore = (tizenVersion?: string) => {
  const connectionDownlink = getConnectionDownlink();

  const downlinkScore = connectionDownlink ? Math.min(connectionDownlink / 10, 1) : 0;

  let score;
  if (downlinkScore < 0.5) {
    score = 'low';
  } else if (tizenVersion) {
    const majorVersion = parseInt(tizenVersion.charAt(0), 10);
    score = majorVersion >= 6 ? 'high' : 'low';
  } else {
    const deviceMemory = getDeviceMemory();
    const hardwareConcurrency = getHardwareConcurrency();
    const memoryScore = deviceMemory ? Math.min(deviceMemory / 8, 1) : 0;
    const cpuScore = hardwareConcurrency ? Math.min(hardwareConcurrency / 8, 1) : 0;
    const averageScore = (memoryScore + cpuScore) / 2;
    score = averageScore < 0.5 ? 'low' : 'high';
  }

  storePersistentData(PERFORMANCE_SCORE_KEY, score, false);
};

const getPerformanceScore = () => {
  return retrievePersistentData<string>(PERFORMANCE_SCORE_KEY);
};

const getDeviceMemory = () => {
  return navigator.deviceMemory;
};

const getConnectionDownlink = () => {
  return navigator.connection?.downlink;
};

const getHardwareConcurrency = () => {
  return navigator.hardwareConcurrency;
};

export {
  calculatePerformanceScore,
  getPerformanceScore,
  getDeviceMemory,
  getConnectionDownlink,
  getHardwareConcurrency
};
